import React from 'react';
import { Alert, App, Button, Input, Space, Typography } from 'antd';
import type { TFunction } from 'i18next';
import { confirmLabel, confirmationNote, overflowRows, warningCopy } from './operationalExceptionText';
import './operationalExceptionFlow.css';
import type { OperationalWarning } from './operationalExceptionText';

type ModalApi = ReturnType<typeof App.useApp>['modal'];

export type { OperationalWarning } from './operationalExceptionText';
// Re-exported so existing callers keep one import; the copy itself lives in
// operationalExceptionText.ts (single home, shared with the Rozbieżności page).
export { warningCopy, warningText } from './operationalExceptionText';

function readConfirmation(error: any) {
  const data = error?.response?.data;
  if (data?.code !== 'OPERATIONAL_EXCEPTION_CONFIRMATION_REQUIRED') return null;
  if (!data.confirmationFingerprint || !Array.isArray(data.warnings)) return null;
  return data as {
    confirmationFingerprint: string;
    warnings: OperationalWarning[];
    reasonRequired?: boolean;
    reasonMinLength?: number;
    reasonMaxLength?: number;
  };
}

/**
 * One warning = one short Polish sentence. Several warnings read as a bulleted
 * list so the operator can scan them. A code the frontend does not know still
 * gets a sentence; its raw code appears only as the small grey support caption.
 */
/**
 * Owner 2026-10-13: an over-allocation (ZK / ZD) reads as a small table, one
 * row per order line, instead of a sentence per line.
 */
const OverflowTable: React.FC<{ table: NonNullable<ReturnType<typeof overflowRows>>; t: TFunction }> = ({ table, t }) => (
  <div className="op-exception-overflow" data-testid={`op-exception-overflow-${table.side}`}>
    <Typography.Text strong>{t(`common:operationalException.overflow.title${table.side}`)}</Typography.Text>
    <table>
      <thead>
        <tr>
          <th>{t('common:operationalException.overflow.line')}</th>
          <th>{t('common:operationalException.overflow.ordered')}</th>
          <th>{t('common:operationalException.overflow.elsewhere')}</th>
          <th>{t('common:operationalException.overflow.attempted')}</th>
          <th>{t('common:operationalException.overflow.excess')}</th>
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row) => (
          <tr key={row.key}>
            <td>
              <div className="op-exception-overflow__line">
                {row.line ? <strong>{row.line}</strong> : null}
                <span className="op-exception-overflow__product">{row.product}</span>
              </div>
            </td>
            <td>{row.ordered}</td>
            <td>{row.elsewhere}</td>
            <td>{row.attempted}</td>
            <td className="op-exception-overflow__excess">{row.excess}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const WarningList: React.FC<{ warnings: OperationalWarning[]; t: TFunction }> = ({ warnings, t }) => {
  const tables = warnings.map((warning) => overflowRows(warning, t));
  const others = warnings.filter((_, index) => tables[index] === null);
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {tables.map((table, index) => (table ? <OverflowTable key={`overflow-${index}`} table={table} t={t} /> : null))}
      {others.length > 0 ? <SentenceList warnings={others} t={t} /> : null}
    </Space>
  );
};

const SentenceList: React.FC<{ warnings: OperationalWarning[]; t: TFunction }> = ({ warnings, t }) => {
  const items = warnings.map((warning, index) => ({
    key: `${warning.code}-${index}`,
    ...warningCopy(warning, t),
  }));
  const body = (item: typeof items[number]) => (
    <>
      <span style={{ whiteSpace: 'pre-line' }}>{item.text}</span>
      {item.supportCode && (
        <>
          {' '}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('common:operationalException.supportCode', { code: item.supportCode })}
          </Typography.Text>
        </>
      )}
    </>
  );
  if (items.length === 1) {
    return <Typography.Paragraph style={{ marginBottom: 0 }}>{body(items[0])}</Typography.Paragraph>;
  }
  return (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {items.map((item) => (
        <li key={item.key} style={{ marginBottom: 4 }}>{body(item)}</li>
      ))}
    </ul>
  );
};

/**
 * Reason-free variant (owner 2026-10-06, e.g. „Gotowe do wydania” mimo
 * braków): the same warning list and button, no reason field. Resolves true
 * on confirm, false on cancel.
 */
async function askForConfirmation(
  modal: ModalApi,
  warnings: OperationalWarning[],
  t: TFunction,
  copy?: OperationalExceptionOptions['confirmationCopy'],
): Promise<boolean> {
  return new Promise((resolve) => {
    modal.confirm({
      title: copy?.title ?? t('common:operationalException.title'),
      width: 620,
      okText: copy?.okText ?? confirmLabel(warnings, t),
      cancelText: t('common:cancel'),
      content: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <WarningList warnings={warnings} t={t} />
          <Typography.Text type="secondary">{copy?.description ?? confirmationNote(warnings, t)}</Typography.Text>
        </Space>
      ),
      onOk: () => { resolve(true); return Promise.resolve(); },
      onCancel: () => resolve(false),
    });
  });
}

/** The operator chose the caller's correction instead of a reason. */
const CORRECT = Symbol('correct');

async function askForReason(
  modal: ModalApi,
  warnings: OperationalWarning[],
  maxLength: number,
  t: TFunction,
  minLength = 1,
  correction?: { label: string; note?: string },
): Promise<string | null | typeof CORRECT> {
  let reason = '';
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: string | null | typeof CORRECT) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const instance = modal.confirm({
      title: t('common:operationalException.title'),
      width: 620,
      okText: confirmLabel(warnings, t),
      cancelText: t('common:cancel'),
      // With a correction on offer it is the primary action; confirming the
      // exception with a reason stays possible as the secondary one.
      ...(correction ? {
        okButtonProps: { type: 'default' as const },
        footer: (_: React.ReactNode, { OkBtn, CancelBtn }: { OkBtn: React.FC; CancelBtn: React.FC }) => (
          <>
            <CancelBtn />
            <OkBtn />
            <Button
              type="primary"
              data-testid="op-exception-correction"
              onClick={() => { settle(CORRECT); instance.destroy(); }}
            >
              {correction.label}
            </Button>
          </>
        ),
      } : {}),
      content: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message={t('common:operationalException.description')}
          />
          <WarningList warnings={warnings} t={t} />
          {correction?.note ? <Typography.Text>{correction.note}</Typography.Text> : null}
          <div>
            <Typography.Text strong>{t('common:operationalException.reasonLabel')}</Typography.Text>
            <Input.TextArea
              rows={3}
              maxLength={maxLength}
              showCount
              placeholder={t('common:operationalException.reasonPlaceholder')}
              onChange={(event) => { reason = event.target.value; }}
            />
            {minLength > 1 && (
              <Typography.Text type="secondary">
                {t('common:operationalException.reasonMinLength', { min: minLength })}
              </Typography.Text>
            )}
          </div>
        </Space>
      ),
      onOk: () => {
        if (!reason.trim()) return Promise.reject(new Error(t('common:operationalException.reasonRequired')));
        if (reason.trim().length < minLength) {
          return Promise.reject(new Error(t('common:operationalException.reasonMinLength', { min: minLength })));
        }
        settle(reason.trim());
        return Promise.resolve();
      },
      onCancel: () => settle(null),
    });
  });
}

export interface OperationalExceptionOptions {
  /**
   * A reason the operator already gave in the calling screen for these
   * warnings (e.g. „Powód wyboru partii” in the „Wystaw WZ” window, where the
   * warning is shown before saving). Used without a second dialog only when
   * every returned warning code is listed and the reason meets the server's
   * minimum length; otherwise the shared dialog asks as usual.
   */
  presetReason?: { codes: string[]; reason: string };
  /**
   * Wording of the reason-free dialog when the command is not a status change
   * (e.g. „Duplikuj auto”, plan 2026-10-13): its default footer speaks of the
   * status history.
   */
  confirmationCopy?: { title?: string; description?: string; okText?: string };
  /**
   * A fix the caller can apply instead of accepting the exception (production
   * ZD 10/43, 2026-10-28: the only way past a load that was not real was a
   * reason). `apply` returns the corrected payload, or null when it cannot fix
   * these warnings — then the dialog offers only the reason. The corrected
   * payload is sent without any confirmation, so the server checks it again.
   */
  correction?: {
    label: string;
    note?: string;
    apply: (warnings: OperationalWarning[], payload: Record<string, unknown>) => Record<string, unknown> | null;
    /** Runs once the operator chose the correction. */
    onApplied?: () => void;
  };
}

function presetReasonFor(
  confirmation: NonNullable<ReturnType<typeof readConfirmation>>,
  options?: OperationalExceptionOptions,
): string | null {
  const preset = options?.presetReason;
  const reason = preset?.reason?.trim() || '';
  if (!preset || !reason) return null;
  const covered = confirmation.warnings.every((warning) => preset.codes.includes(warning.code));
  const longEnough = reason.length >= (confirmation.reasonMinLength || 1)
    && reason.length <= (confirmation.reasonMaxLength || 500);
  return covered && longEnough ? reason : null;
}

export async function submitWithOperationalException<T>(
  request: (payload: Record<string, unknown>) => Promise<T>,
  payload: Record<string, unknown>,
  modal: ModalApi,
  t: TFunction,
  options?: OperationalExceptionOptions,
): Promise<T | null> {
  let basePayload = payload;
  let nextPayload = payload;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      return await request(nextPayload);
    } catch (error) {
      const confirmation = readConfirmation(error);
      if (!confirmation) throw error;
      if (confirmation.reasonRequired === false) {
        if (!(await askForConfirmation(modal, confirmation.warnings, t, options?.confirmationCopy))) return null;
        nextPayload = {
          ...basePayload,
          confirmOperationalException: true,
          operationalExceptionFingerprint: confirmation.confirmationFingerprint,
        };
        continue;
      }
      const corrected = options?.correction?.apply(confirmation.warnings, basePayload) ?? null;
      const answer = presetReasonFor(confirmation, options) ?? await askForReason(
        modal,
        confirmation.warnings,
        confirmation.reasonMaxLength || 500,
        t,
        confirmation.reasonMinLength || 1,
        corrected && options?.correction ? { label: options.correction.label, note: options.correction.note } : undefined,
      );
      if (answer === CORRECT && corrected) {
        options?.correction?.onApplied?.();
        basePayload = corrected;
        nextPayload = corrected;
        continue;
      }
      if (!answer || answer === CORRECT) return null;
      const reason = answer;
      nextPayload = {
        ...basePayload,
        confirmOperationalException: true,
        operationalExceptionFingerprint: confirmation.confirmationFingerprint,
        operationalExceptionReason: reason,
      };
    }
  }
  throw new Error('Operational exception confirmation changed repeatedly');
}
